import { useState, useMemo } from 'react';
import { useParams } from 'react-router';
import { SortingState } from '@tanstack/react-table';
import { PaginationState, TableSkeleton } from '@/components';
import {
  useGetPatientMedicalRecordsCounts,
  useGetPatientProviderNotesSummaries,
  useGetPatientImagingSummaries,
  useGetPatientProceduresSummaries,
  useGetPatientLabResultsSummaries,
  useGetPatientPathologyResultsSummaries,
  useGetPatientMedicationsSummaries,
  useGetPatientVitalsSummaries,
  useGetPatientProblemsSummaries,
  useGetPatientEncountersSummaries,
  useGetPatientAdditionalRecordsSummaries,
  MedicalRecordsSortColumn,
} from '../../../../hooks/medicalRecordsAPI';
import { MedicalRecordsSidebar } from './MedicalRecordsSidebar';
import { MedicalRecordsTable } from './MedicalRecordsTable';
import { AnyMedicalRecord, MedicalRecordType } from './types';
import { DEFAULT_RECORD_TYPE } from './constants';

// The new viewer pattern handles its own sheet, so this section accepts no
// view-document callback. Kept as a no-prop component for clarity.
export const MedicalRecordsSection = () => {
  const { patientId } = useParams<{ patientId: string }>();

  const [selectedType, setSelectedType] =
    useState<MedicalRecordType>(DEFAULT_RECORD_TYPE);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Convert sorting state to API format
  const sortColumns: MedicalRecordsSortColumn[] | undefined = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return sorting.map((sort) => ({
      column: sort.id as 'sourceType' | 'dateUploaded' | 'dateOfStudy',
      order: sort.desc ? ('desc' as const) : ('asc' as const),
    }));
  }, [sorting]);

  // Fetch counts
  const { data: countsData, isLoading: countsLoading } =
    useGetPatientMedicalRecordsCounts(patientId);

  // Build query params
  const queryParams = useMemo(
    () => ({
      patientId: patientId || '',
      pagination: {
        offset: pagination.page * pagination.pageSize,
        limit: pagination.pageSize,
      },
      sortColumns,
    }),
    [patientId, pagination, sortColumns],
  );

  // Fetch records based on selected type
  const providerNotesQuery = useGetPatientProviderNotesSummaries(
    selectedType === 'providerNotes' ? queryParams : undefined,
  );
  const imagingQuery = useGetPatientImagingSummaries(
    selectedType === 'imaging' ? queryParams : undefined,
  );
  const proceduresQuery = useGetPatientProceduresSummaries(
    selectedType === 'procedures' ? queryParams : undefined,
  );
  const labResultsQuery = useGetPatientLabResultsSummaries(
    selectedType === 'labResults' ? queryParams : undefined,
  );
  const pathologyResultsQuery = useGetPatientPathologyResultsSummaries(
    selectedType === 'pathologyResults' ? queryParams : undefined,
  );
  const medicationsQuery = useGetPatientMedicationsSummaries(
    selectedType === 'medications' ? queryParams : undefined,
  );
  const vitalsQuery = useGetPatientVitalsSummaries(
    selectedType === 'vitals' ? queryParams : undefined,
  );
  const problemsQuery = useGetPatientProblemsSummaries(
    selectedType === 'problems' ? queryParams : undefined,
  );
  const encountersQuery = useGetPatientEncountersSummaries(
    selectedType === 'encounters' ? queryParams : undefined,
  );
  const additionalRecordsQuery = useGetPatientAdditionalRecordsSummaries(
    selectedType === 'additionalRecords' ? queryParams : undefined,
  );

  // Map selected type to its query
  const queryMap = {
    providerNotes: providerNotesQuery,
    imaging: imagingQuery,
    procedures: proceduresQuery,
    labResults: labResultsQuery,
    pathologyResults: pathologyResultsQuery,
    medications: medicationsQuery,
    vitals: vitalsQuery,
    problems: problemsQuery,
    encounters: encountersQuery,
    additionalRecords: additionalRecordsQuery,
  } as const;

  const currentQuery = queryMap[selectedType];

  const records = currentQuery?.data?.records || [];
  const totalCount = currentQuery?.data?.totalCount || 0;
  const recordsLoading = currentQuery?.isLoading || false;

  const handleTypeChange = (type: MedicalRecordType) => {
    setSelectedType(type);
    setPagination({ page: 0, pageSize: pagination.pageSize });
    setSorting([]);
  };

  // Loading skeleton for initial counts load
  if (countsLoading) {
    return (
      <div className="flex gap-6">
        <div className="w-56 shrink-0 rounded-lg bg-gray-50 p-3">
          <div className="space-y-0.5">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-1.5"
              >
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-5 w-6 animate-pulse rounded-full bg-gray-200" />
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <TableSkeleton rows={5} columns={5} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-6">
      <MedicalRecordsSidebar
        counts={countsData}
        selectedType={selectedType}
        onSelectType={handleTypeChange}
        isLoading={countsLoading}
      />
      <MedicalRecordsTable
        recordType={selectedType}
        records={records as AnyMedicalRecord[]}
        totalCount={totalCount}
        isLoading={recordsLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </div>
  );
};
